import { db } from '../firebase';
import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy, where, serverTimestamp } from 'firebase/firestore';
import { toJsDate } from '../utils/firestoreDate';

export interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  totalOrders: number;
  totalSpent: number;
  lastOrderDate: any;
  lastOrderNumber?: string;
  lastOrderAmount?: number;
  createdAt: any;
  status: 'active' | 'inactive' | 'vip';
  notes?: string;
  tags?: string[];
  preferences?: {
    newsletter: boolean;
    marketing: boolean;
    notifications: boolean;
  };
}

export interface CustomerAnalytics {
  totalCustomers: number;
  activeCustomers: number;
  vipCustomers: number;
  inactiveCustomers: number;
  totalRevenue: number;
  avgOrderValue: number;
  avgOrdersPerCustomer: number;
  topSpenders: Customer[];
  recentCustomers: Customer[];
  customerGrowth: {
    month: string;
    count: number;
  }[];
}

export interface CustomerFilters {
  status?: 'all' | 'active' | 'inactive' | 'vip';
  searchTerm?: string;
  sortBy?: 'name' | 'spent' | 'orders' | 'date' | 'status';
  sortOrder?: 'asc' | 'desc';
  dateRange?: {
    start: Date;
    end: Date;
  };
  tags?: string[];
}

class CustomerService {
  private customersCollection = 'customers';

  // Get all customers
  async getAllCustomers(): Promise<Customer[]> {
    try {
      const customersRef = collection(db, this.customersCollection);
      const q = query(customersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const customers: Customer[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        customers.push({
          id: doc.id,
          ...data,
          createdAt: toJsDate(data.createdAt) ?? new Date(),
          lastOrderDate: toJsDate(data.lastOrderDate),
        } as Customer);
      });

      return customers;
    } catch (error: any) {
      // Check if it's a permission error - silently handle to reduce console spam
      // Firebase errors have code 'permission-denied' and message containing 'permission' or 'insufficient'
      try {
        const errorStr = JSON.stringify(error).toLowerCase();
        const errorMessage = String(error?.message || '').toLowerCase();
        const errorCode = String(error?.code || '').toLowerCase();
        const errorName = String(error?.name || '').toLowerCase();
        
        // Comprehensive check for permission errors - check all possible representations
        const isPermissionError = 
          errorCode === 'permission-denied' || 
          errorCode.includes('permission') ||
          errorMessage.includes('permission') ||
          errorMessage.includes('insufficient') ||
          errorMessage.includes('missing or insufficient') ||
          errorStr.includes('permission') ||
          errorStr.includes('insufficient') ||
          (errorName.includes('firebase') && (errorMessage.includes('permission') || errorMessage.includes('insufficient')));
        
        if (isPermissionError) {
          // Silently return empty array for permission errors
          // The rules need to be deployed to Firebase Console
          // Rules file is updated at firestore.rules - deploy via Firebase Console
          return [];
        }
      } catch {
        // If error parsing fails, check the string representation
        const errorStr = String(error || '').toLowerCase();
        if (errorStr.includes('permission') || errorStr.includes('insufficient')) {
          return [];
        }
      }
      
      // Log other errors normally
      console.error('Error fetching customers:', error);
      return [];
    }
  }

  // Get customer by ID
  async getCustomerById(id: string): Promise<Customer | null> {
    try {
      const customerDoc = await getDocs(query(collection(db, this.customersCollection), where('__name__', '==', id)));
      
      if (!customerDoc.empty) {
        const docSnapshot = customerDoc.docs[0];
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          createdAt: toJsDate(data.createdAt) ?? new Date(),
          lastOrderDate: toJsDate(data.lastOrderDate),
        } as Customer;
      }
      return null;
    } catch (error) {
      console.error('Error fetching customer:', error);
      return null;
    }
  }

  // Get customer by email
  async getCustomerByEmail(email: string): Promise<Customer | null> {
    try {
      const customersRef = collection(db, this.customersCollection);
      const q = query(customersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const docSnapshot = querySnapshot.docs[0];
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          createdAt: toJsDate(data.createdAt) ?? new Date(),
          lastOrderDate: toJsDate(data.lastOrderDate),
        } as Customer;
      }
      return null;
    } catch (error) {
      console.error('Error fetching customer by email:', error);
      return null;
    }
  }

  // Create new customer
  async createCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'totalOrders' | 'totalSpent' | 'lastOrderDate'>): Promise<Customer> {
    try {
      const fullName = customerData.fullName || `${customerData.firstName} ${customerData.lastName}`.trim();
      
      const customerDoc = {
        ...customerData,
        fullName: fullName,
        status: customerData.status || 'active',
        totalOrders: 0,
        totalSpent: 0,
        lastOrderDate: null,
        createdAt: serverTimestamp(),
        notes: customerData.notes || '',
        tags: customerData.tags || [],
        preferences: customerData.preferences || {
          newsletter: true,
          marketing: false,
          notifications: true
        }
      };

      const customersRef = collection(db, this.customersCollection);
      const docRef = await addDoc(customersRef, customerDoc);
      
      const newCustomer: Customer = {
        id: docRef.id,
        ...customerData,
        fullName: fullName,
        status: customerData.status || 'active',
        totalOrders: 0,
        totalSpent: 0,
        lastOrderDate: null,
        createdAt: new Date(),
        notes: customerData.notes || '',
        tags: customerData.tags || [],
        preferences: customerData.preferences || {
          newsletter: true,
          marketing: false,
          notifications: true
        }
      };

      return newCustomer;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  // Update customer
  async updateCustomer(id: string, customerData: Partial<Customer>): Promise<Customer> {
    try {
      const customerRef = doc(db, this.customersCollection, id);
      
      const updateData: any = { ...customerData };
      
      if (customerData.firstName || customerData.lastName) {
        const existingCustomer = await this.getCustomerById(id);
        if (existingCustomer) {
          const newFirstName = customerData.firstName || existingCustomer.firstName;
          const newLastName = customerData.lastName || existingCustomer.lastName;
          updateData.fullName = `${newFirstName} ${newLastName}`;
        }
      }

      await updateDoc(customerRef, updateData);

      const updatedCustomer = await this.getCustomerById(id);
      if (!updatedCustomer) {
        throw new Error('Customer not found after update');
      }

      return updatedCustomer;
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  }

  // Update customer from order
  async updateCustomerFromOrder(customerId: string, orderData: {
    orderId?: string;
    orderNumber?: string;
    totalAmount?: number;
    lastOrderDate?: Date;
  }): Promise<Customer> {
    try {
      const customerRef = doc(db, this.customersCollection, customerId);
      
      const currentCustomer = await this.getCustomerById(customerId);
      if (!currentCustomer) {
        throw new Error('Customer not found');
      }
      
      const updateData: any = {
        totalOrders: (currentCustomer.totalOrders || 0) + 1,
        totalSpent: (currentCustomer.totalSpent || 0) + (orderData.totalAmount || 0),
        lastOrderDate: orderData.lastOrderDate || new Date(),
        lastOrderNumber: orderData.orderNumber,
        lastOrderAmount: orderData.totalAmount
      };
      
      if (orderData.orderNumber) {
        const existingNotes = currentCustomer.notes || '';
        const orderNote = `\n[${new Date().toLocaleDateString()}] Order: ${orderData.orderNumber} - $${orderData.totalAmount?.toFixed(2) || '0.00'}`;
        updateData.notes = `${existingNotes}${orderNote}`;
      }
      
      await updateDoc(customerRef, updateData);
      
      const updatedCustomer = await this.getCustomerById(customerId);
      if (!updatedCustomer) {
        throw new Error('Customer not found after update');
      }
      
      return updatedCustomer;
    } catch (error) {
      console.error('Error updating customer from order:', error);
      throw error;
    }
  }

  // Delete customer
  async deleteCustomer(id: string): Promise<boolean> {
    try {
      const customerRef = doc(db, this.customersCollection, id);
      await deleteDoc(customerRef);
      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      return false;
    }
  }

  // Search customers
  async searchCustomers(filters: CustomerFilters): Promise<Customer[]> {
    try {
      return await this.getAllCustomers();
    } catch (error) {
      console.error('Error searching customers:', error);
      return [];
    }
  }

  // Get customer analytics
  async getCustomerAnalytics(): Promise<CustomerAnalytics> {
    try {
      const customers = await this.getAllCustomers();
      // If getAllCustomers returns empty due to permission error, return default analytics
      
      const totalCustomers = customers.length;
      const activeCustomers = customers.filter(c => c.status === 'active').length;
      const vipCustomers = customers.filter(c => c.status === 'vip').length;
      const inactiveCustomers = customers.filter(c => c.status === 'inactive').length;
      
      const totalRevenue = customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0);
      const totalOrders = customers.reduce((sum, c) => sum + (c.totalOrders || 0), 0);
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      const avgOrdersPerCustomer = totalCustomers > 0 ? totalOrders / totalCustomers : 0;
      
      const topSpenders = [...customers]
        .sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0))
        .slice(0, 10);
      
      const recentCustomers = [...customers]
        .sort((a, b) => {
          const aDate = toJsDate(a.createdAt) ?? new Date(0);
          const bDate = toJsDate(b.createdAt) ?? new Date(0);
          return bDate.getTime() - aDate.getTime();
        })
        .slice(0, 10);
      
      // Calculate customer growth by month (last 12 months)
      const customerGrowth: { month: string; count: number }[] = [];
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        
        const count = customers.filter(c => {
          const createdDate = toJsDate(c.createdAt);
          return createdDate != null && createdDate >= monthStart && createdDate <= monthEnd;
        }).length;
        
        customerGrowth.push({ month: monthStr, count });
      }
      
      return {
        totalCustomers,
        activeCustomers,
        vipCustomers,
        inactiveCustomers,
        totalRevenue,
        avgOrderValue,
        avgOrdersPerCustomer,
        topSpenders,
        recentCustomers,
        customerGrowth
      };
    } catch (error) {
      console.error('Error fetching customer analytics:', error);
      return {
        totalCustomers: 0,
        activeCustomers: 0,
        vipCustomers: 0,
        inactiveCustomers: 0,
        totalRevenue: 0,
        avgOrderValue: 0,
        avgOrdersPerCustomer: 0,
        topSpenders: [],
        recentCustomers: [],
        customerGrowth: []
      };
    }
  }

  // Update customer status
  async updateCustomerStatus(id: string, status: Customer['status']): Promise<Customer> {
    return this.updateCustomer(id, { status });
  }

  // Add customer note
  async addCustomerNote(id: string, note: string): Promise<Customer> {
    const customer = await this.getCustomerById(id);
    if (!customer) {
      throw new Error('Customer not found');
    }
    const existingNotes = customer.notes || '';
    const newNote = existingNotes ? `${existingNotes}\n${note}` : note;
    return this.updateCustomer(id, { notes: newNote });
  }

  // Add customer tag
  async addCustomerTag(id: string, tag: string): Promise<Customer> {
    const customer = await this.getCustomerById(id);
    if (!customer) {
      throw new Error('Customer not found');
    }
    const existingTags = customer.tags || [];
    if (!existingTags.includes(tag)) {
      return this.updateCustomer(id, { tags: [...existingTags, tag] });
    }
    return customer;
  }

  // Remove customer tag
  async removeCustomerTag(id: string, tag: string): Promise<Customer> {
    const customer = await this.getCustomerById(id);
    if (!customer) {
      throw new Error('Customer not found');
    }
    const existingTags = customer.tags || [];
    return this.updateCustomer(id, { tags: existingTags.filter(t => t !== tag) });
  }

  // Update customer preferences
  async updateCustomerPreferences(id: string, preferences: Customer['preferences']): Promise<Customer> {
    return this.updateCustomer(id, { preferences });
  }

  // Export customers to CSV
  async exportCustomers(filters?: CustomerFilters): Promise<string> {
    try {
      const customers = await this.searchCustomers(filters || {});
      
      // CSV header
      const headers = ['ID', 'Name', 'Email', 'Phone', 'Status', 'Total Orders', 'Total Spent', 'Last Order Date', 'Created At'];
      const rows = customers.map(customer => [
        customer.id,
        customer.fullName,
        customer.email,
        customer.phone || '',
        customer.status,
        customer.totalOrders.toString(),
        customer.totalSpent.toString(),
        toJsDate(customer.lastOrderDate)?.toISOString() ?? '',
        toJsDate(customer.createdAt)?.toISOString() ?? ''
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      return csvContent;
    } catch (error) {
      console.error('Error exporting customers:', error);
      throw error;
    }
  }

  // Bulk update customer status
  async bulkUpdateCustomerStatus(customerIds: string[], status: Customer['status']): Promise<void> {
    try {
      const updatePromises = customerIds.map(id => this.updateCustomerStatus(id, status));
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error bulk updating customer status:', error);
      throw error;
    }
  }

  // Send customer message (placeholder - would integrate with messaging service)
  async sendCustomerMessage(customerId: string, message: string, type: 'email' | 'sms' | 'notification'): Promise<void> {
    try {
      const customer = await this.getCustomerById(customerId);
      if (!customer) {
        throw new Error('Customer not found');
      }
      
      // This is a placeholder - would integrate with actual messaging service
      console.log(`Sending ${type} message to customer ${customerId}:`, message);
      
      // Add note to customer record
      await this.addCustomerNote(customerId, `[${type.toUpperCase()}] ${message}`);
    } catch (error) {
      console.error('Error sending customer message:', error);
      throw error;
    }
  }

  // Create customer from checkout data
  async createCustomerFromCheckout(checkoutData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    shippingAddress: {
      firstName: string;
      lastName: string;
      address: string;
      address2?: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      email?: string;
      company?: string;
      deliveryInstructions?: string;
      phone?: string;
    };
    billingAddress: {
      firstName: string;
      lastName: string;
      email: string;
      address: string;
      address2?: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      company?: string;
      notes?: string;
    };
    orderId?: string;
    orderNumber?: string;
    totalAmount?: number;
  }): Promise<Customer> {
    try {
      // Check if customer already exists
      const existingCustomer = await this.getCustomerByEmail(checkoutData.email);
      if (existingCustomer) {
        // Update existing customer with order info
        return this.updateCustomerFromOrder(existingCustomer.id, {
          orderId: checkoutData.orderId,
          orderNumber: checkoutData.orderNumber,
          totalAmount: checkoutData.totalAmount,
          lastOrderDate: new Date()
        });
      }

      // Create new customer
      const customerData: Omit<Customer, 'id' | 'createdAt' | 'totalOrders' | 'totalSpent' | 'lastOrderDate'> = {
        firstName: checkoutData.firstName,
        lastName: checkoutData.lastName,
        email: checkoutData.email,
        phone: checkoutData.phone || '',
        fullName: `${checkoutData.firstName} ${checkoutData.lastName}`,
        address: {
          street: checkoutData.shippingAddress.address,
          city: checkoutData.shippingAddress.city,
          state: checkoutData.shippingAddress.state,
          zip: checkoutData.shippingAddress.zipCode,
          country: checkoutData.shippingAddress.country
        },
        status: 'active',
        notes: checkoutData.billingAddress.notes || '',
        tags: [],
        preferences: {
          newsletter: true,
          marketing: false,
          notifications: true
        }
      };

      const newCustomer = await this.createCustomer(customerData);
      
      // If there's order data, update the customer
      if (checkoutData.orderId || checkoutData.orderNumber || checkoutData.totalAmount) {
        return this.updateCustomerFromOrder(newCustomer.id, {
          orderId: checkoutData.orderId,
          orderNumber: checkoutData.orderNumber,
          totalAmount: checkoutData.totalAmount,
          lastOrderDate: new Date()
        });
      }

      return newCustomer;
    } catch (error) {
      console.error('Error creating customer from checkout:', error);
      throw error;
    }
  }
}

const customerService = new CustomerService();
export default customerService;
